import { useCallback, useEffect, useMemo, useState } from 'react';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Switch,
  type SxProps,
  TextField,
  type Theme,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { CustomUser } from 'api/models/CustomUser';
import type { ColType } from 'common/types/summaryDefinitions';
import type { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';
import { useZoom } from 'context/ZoomContext';
import { FilterTypeSelection, modelsStatusOptions } from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { MODELS_COLUMN_IDS, MODELS_COLUMN_LABELS } from 'dashboard409a/pages/Workboard/common/constants';
import { modelsColumns } from 'dashboard409a/pages/Workboard/common/modelsColConfig';
import {
  DEFAULT_TRAILING_MONTHS,
  getTrailingDateRange,
  type TrailingMonthsOption,
} from 'dashboard409a/pages/Workboard/common/trailingDates';
import {
  GroupedUserAutocomplete,
  type GroupedUserAutocompleteDisplayItem,
} from 'dashboard409a/pages/Workboard/Filters/GroupedUserAutocomplete';
import type { SelectedUserWithGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import EditColumnsPanel from 'pages/Firms/pages/FirmSummary/EditColumnsPanel';
import { ModelView, ViewUserId } from '../ModelViewsSelector/ModelViewsSelector.props';

const DEFAULT_FILTER_TYPE_SELECTION: FilterTypeSelection = { assigned: true, unassigned: false, all: false };

// The 8 toggleable column ids (all except company_name)
const MODELS_TOGGLEABLE_COLUMN_IDS: string[] = modelsColumns
  .filter(col => col.id !== MODELS_COLUMN_IDS.COMPANY_NAME)
  .map(col => col.id);

// Build ValueType objects for EditColumnsPanel
const modelsValueTypes: ValueType[] = MODELS_TOGGLEABLE_COLUMN_IDS.map(id => {
  const labelKey = Object.entries(MODELS_COLUMN_IDS).find(([, v]) => v === id)?.[0] as
    | keyof typeof MODELS_COLUMN_LABELS
    | undefined;
  const displayName = labelKey ? MODELS_COLUMN_LABELS[labelKey] : id;
  return {
    id,
    displayName,
    displayFormat: { type: 'text' as const },
    kind: 'simple' as const,
    dependsOn: [],
    metadata: { category: 'process' as const },
  };
});

const modelsValueTypeMap = new Map(modelsValueTypes.map(vt => [vt.id, vt]));

const DEFAULT_MODELS_COLUMNS = MODELS_TOGGLEABLE_COLUMN_IDS as ColType[];

const columnsFromVisibility = (columnVisibility: Record<string, boolean>): ColType[] => {
  const visibleCols = MODELS_TOGGLEABLE_COLUMN_IDS.filter(id => columnVisibility[id] !== false) as ColType[];
  return visibleCols.length > 0 ? visibleCols : DEFAULT_MODELS_COLUMNS;
};

const isPrivateVisibility = (visibility?: ModelView['visibility']): boolean => visibility !== 'public';

// Columns that cannot be removed or reordered (company_name is non-toggleable, handled separately)
const MODELS_REQUIRED_COLUMNS = new Set([
  MODELS_COLUMN_IDS.VALUATION_STATUS,
  MODELS_COLUMN_IDS.MODEL_STATUS,
  MODELS_COLUMN_IDS.NOTES,
  MODELS_COLUMN_IDS.MODEL_REVIEWER,
  MODELS_COLUMN_IDS.MODEL_PREPARER,
]);

// Compact textField sx for GroupedUserAutocomplete inside the dialog
const autocompleteInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': {
    minHeight: '32px',
    height: '32px',
    flexWrap: 'nowrap',
    py: '0 !important',
    fontSize: '0.8125rem',
  },
  '& .MuiAutocomplete-input': {
    py: '0 !important',
    fontSize: '0.8125rem',
    minWidth: '20px',
  },
  '& .MuiInputLabel-root': { display: 'none' },
  '& .MuiInputLabel-shrink': { display: 'none' },
};

export interface ModelsNewViewDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateView: (view: ModelView) => void;
  currentColumnVisibility: Record<string, boolean>;
  staffUsers: CustomUser[];
  defaultName?: string;
  /** When provided, the dialog operates in edit mode (pre-populated, Save button). */
  initialView?: ModelView;
}

/**
 * Helper: convert ViewUserId[] + a staff users map to SelectedUserWithGroup[].
 * We pass a Map<number, SelectedUserWithGroup> for O(1) lookup.
 */
const viewUserIdsToSelected = (
  viewUserIds: ViewUserId[],
  userMap: Map<number, SelectedUserWithGroup>
): SelectedUserWithGroup[] =>
  viewUserIds
    .map(({ userId, groupId }) => {
      const user = userMap.get(userId);
      if (!user) {
        return null;
      }
      return { ...user, fromGroupId: groupId ?? undefined };
    })
    .filter((u): u is SelectedUserWithGroup => u !== null);

const ModelsNewViewDialog = ({
  open,
  onClose,
  onCreateView,
  currentColumnVisibility,
  staffUsers,
  defaultName,
  initialView,
}: ModelsNewViewDialogProps) => {
  const isEditMode = Boolean(initialView);
  const { zoomLevel } = useZoom();

  const [viewName, setViewName] = useState('');
  const [editorColumns, setEditorColumns] = useState<ColType[]>(DEFAULT_MODELS_COLUMNS);
  const [searchStatus, setSearchStatus] = useState<string[]>([]);
  const [reviewers, setReviewers] = useState<SelectedUserWithGroup[]>([]);
  const [preparers, setPreparers] = useState<SelectedUserWithGroup[]>([]);
  const [reviewerInput, setReviewerInput] = useState('');
  const [preparerInput, setPreparerInput] = useState('');
  const [filterTypeSelection, setFilterTypeSelection] = useState<FilterTypeSelection>(DEFAULT_FILTER_TYPE_SELECTION);
  const [useTrailingDates, setUseTrailingDates] = useState(false);
  const [trailingMonths, setTrailingMonths] = useState<TrailingMonthsOption>(3);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [isPrivateView, setIsPrivateView] = useState(true);

  const staffUserMap = useMemo<Map<number, SelectedUserWithGroup>>(
    () => new Map((staffUsers as SelectedUserWithGroup[]).filter(u => u.id != null).map(u => [u.id as number, u])),
    [staffUsers]
  );

  // Initialize / reset state when dialog opens
  useEffect(() => {
    if (!open) {
      return;
    }

    if (initialView) {
      const columnVisibility = initialView.columnVisibility ?? {};

      let statusArray: string[] = [];
      if (Array.isArray(initialView.searchStatus)) {
        statusArray = initialView.searchStatus;
      } else if (initialView.searchStatus) {
        statusArray = [initialView.searchStatus];
      }

      setViewName(initialView.name);
      setEditorColumns(columnsFromVisibility(columnVisibility));
      setSearchStatus(statusArray);
      setFilterTypeSelection(initialView.filterTypeSelection ?? DEFAULT_FILTER_TYPE_SELECTION);
      setUseTrailingDates(initialView.useTrailingDates ?? false);
      setTrailingMonths(initialView.trailingMonths ?? DEFAULT_TRAILING_MONTHS);
      if (initialView.useTrailingDates) {
        setFromDate(null);
        setToDate(null);
      } else {
        setFromDate(initialView.fromDate ? new Date(initialView.fromDate) : null);
        setToDate(initialView.toDate ? new Date(initialView.toDate) : null);
      }
      setIsPrivateView(isPrivateVisibility(initialView.visibility));
      setReviewerInput('');
      setPreparerInput('');
      setReviewers(viewUserIdsToSelected(initialView.reviewerIds, staffUserMap));
      setPreparers(viewUserIdsToSelected(initialView.preparerIds, staffUserMap));
    } else {
      setViewName(defaultName ?? '');
      setEditorColumns(columnsFromVisibility(currentColumnVisibility));
      setSearchStatus([]);
      setReviewers([]);
      setPreparers([]);
      setReviewerInput('');
      setPreparerInput('');
      setFilterTypeSelection(DEFAULT_FILTER_TYPE_SELECTION);
      setUseTrailingDates(true);
      setTrailingMonths(DEFAULT_TRAILING_MONTHS);
      setFromDate(null);
      setToDate(null);
      setIsPrivateView(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialView?.id]);

  const handleFilterTypeToggle = (key: keyof FilterTypeSelection) => {
    setFilterTypeSelection(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClearFilters = () => {
    setSearchStatus([]);
    setReviewers([]);
    setPreparers([]);
    setReviewerInput('');
    setPreparerInput('');
    setFilterTypeSelection(DEFAULT_FILTER_TYPE_SELECTION);
    setUseTrailingDates(true);
    setTrailingMonths(DEFAULT_TRAILING_MONTHS);
    setFromDate(null);
    setToDate(null);
  };

  // Chip-with-name renderTags for the dialog
  const renderDialogTags = useCallback(
    (displayItems: GroupedUserAutocompleteDisplayItem[], maxVisible: number) => {
      if (displayItems.length === 0) {
        return null;
      }
      const visible = displayItems.slice(0, maxVisible);
      const remaining = displayItems.length - maxVisible;
      const chipSx: SxProps<Theme> = {
        height: 20,
        fontSize: '0.7rem',
        bgcolor: 'action.hover',
        '& .MuiChip-label': { px: 0.5 },
        '& .MuiChip-icon': { fontSize: '0.75rem', ml: '4px' },
      };
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'nowrap', overflow: 'hidden', mr: 0.5 }}>
          {visible.map((item, i) => (
            <Chip
              key={i}
              size="small"
              icon={<AccountCircleIcon />}
              label={
                item.kind === 'group'
                  ? item.data.name
                  : [item.data.first_name, item.data.last_name].filter(Boolean).join(' ') ||
                    item.data.email ||
                    ''
              }
              sx={chipSx}
            />
          ))}
          {remaining > 0 && (
            <Chip
              size="small"
              icon={<AccountCircleIcon />}
              label={`+${remaining}`}
              sx={chipSx}
            />
          )}
        </Box>
      );
    },
    []
  );

  const handleCreate = () => {
    if (!viewName.trim()) {
      return;
    }

    const resolvedDates = useTrailingDates
      ? getTrailingDateRange(trailingMonths)
      : { fromDate, toDate };

    // Build columnVisibility from selected columns
    const columnVisibility: Record<string, boolean> = {};
    MODELS_TOGGLEABLE_COLUMN_IDS.forEach(id => {
      columnVisibility[id] = (editorColumns as string[]).includes(id);
    });

    // Convert SelectedUserWithGroup[] -> ViewUserId[]
    const toViewUserIds = (users: SelectedUserWithGroup[]): ViewUserId[] =>
      users
        .filter(u => u.id !== undefined)
        .map(u => ({ userId: u.id as number, groupId: u.fromGroupId ?? null }));

    onCreateView({
      id: initialView?.id ?? crypto.randomUUID(),
      name: viewName.trim(),
      preparerIds: toViewUserIds(preparers),
      reviewerIds: toViewUserIds(reviewers),
      searchStatus,
      useTrailingDates,
      trailingMonths: useTrailingDates ? trailingMonths : undefined,
      fromDate: resolvedDates.fromDate,
      toDate: resolvedDates.toDate,
      filterTypeSelection,
      columnVisibility,
      visibility: isPrivateView ? 'private' : 'public',
      createdBy: initialView?.createdBy,
    });
    onClose();
  };

  const isNameEmpty = viewName.trim().length === 0;

  // Memoize status options for multi-select
  const statusMenuItems = useMemo(
    () =>
      modelsStatusOptions.map(opt => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      )),
    []
  );

  return (
    <Dialog
      maxWidth="md"
      fullWidth
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        {isEditMode ? 'Edit View' : 'Create Models View'}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Name your view, choose which columns to display, and set filters.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Name */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Name
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={viewName}
          onChange={e => setViewName(e.target.value)}
          error={isNameEmpty && open}
          helperText={isNameEmpty && open ? 'Name is required.' : ' '}
          onKeyDown={e => {
            if (e.key === 'Enter' && !isNameEmpty) {
              handleCreate();
            }
          }}
          autoFocus
        />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5, mb: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={isPrivateView}
                onChange={(_, checked) => setIsPrivateView(checked)}
              />
            }
            label={<Typography variant="body2">Private view</Typography>}
            sx={{ mx: 0 }}
          />
          <Typography variant="caption" color="text.secondary">
            {isPrivateView
              ? 'Only you can see this view.'
              : 'Visible to all analysts.'}
          </Typography>
        </Stack>


        {/* Filters */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Filters
        </Typography>

        {/* Row 1: Model Status | Reviewers | Preparers */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>
              Model Status
            </Typography>
            <Select
              size="small"
              multiple
              value={searchStatus}
              onChange={(e: SelectChangeEvent<string[]>) => {
                const val = e.target.value;
                setSearchStatus(typeof val === 'string' ? val.split(',') : val);
              }}
              displayEmpty
              renderValue={selected =>
                (selected as string[]).length === 0
                  ? 'All'
                  : (selected as string[])
                    .map(v => modelsStatusOptions.find(o => o.value === v)?.label ?? v)
                    .join(', ')
              }
              sx={{ minWidth: 160, '& .MuiSelect-select': { py: '5px', fontSize: '0.8125rem' } }}
            >
              {statusMenuItems}
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>
              Reviewers
            </Typography>
            <GroupedUserAutocomplete
              value={reviewers}
              onChange={setReviewers}
              inputValue={reviewerInput}
              onInputChange={setReviewerInput}
              label=""
              placeholder="Select User"
              zoomLevel={zoomLevel}
              maxVisibleTags={2}
              renderTagsOverride={renderDialogTags}
              textFieldSx={autocompleteInputSx}
              autocompleteRootSx={{ minWidth: 160 }}
            />
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={500}>
              Preparers
            </Typography>
            <GroupedUserAutocomplete
              value={preparers}
              onChange={setPreparers}
              inputValue={preparerInput}
              onInputChange={setPreparerInput}
              label=""
              placeholder="Select User"
              zoomLevel={zoomLevel}
              maxVisibleTags={2}
              renderTagsOverride={renderDialogTags}
              textFieldSx={autocompleteInputSx}
              autocompleteRootSx={{ minWidth: 160 }}
            />
          </Stack>
        </Stack>

        {/* Row 2: Assigned / Unassigned / All */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          {(['assigned', 'unassigned', 'all'] as const).map(key => (
            <FormControlLabel
              key={key}
              labelPlacement="start"
              control={
                <Checkbox
                  size="small"
                  checked={filterTypeSelection[key]}
                  onChange={() => handleFilterTypeToggle(key)}
                />
              }
              label={
                <Typography variant="body2">
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </Typography>
              }
              sx={{ mx: 0, ml: 0 }}
            />
          ))}
        </Stack>

        {/* Row 3: Use Trailing Dates + months or date pickers + Clear Filters */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
          <FormControlLabel
            labelPlacement="start"
            control={
              <Checkbox
                size="small"
                checked={useTrailingDates}
                onChange={(_, checked) => setUseTrailingDates(checked)}
              />
            }
            label={<Typography variant="body2">Use Trailing Dates</Typography>}
            sx={{ mx: 0, ml: 0 }}
          />
          {useTrailingDates ? (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={trailingMonths}
              onChange={(_, v: TrailingMonthsOption | null) => {
                if (v) {
                  setTrailingMonths(v);
                }
              }}
              sx={{ '& .MuiToggleButton-root': { py: '3px', fontSize: '0.75rem' } }}
            >
              <ToggleButton value={1}>1 Month</ToggleButton>
              <ToggleButton value={2}>2 Month</ToggleButton>
              <ToggleButton value={3}>3 Month</ToggleButton>
            </ToggleButtonGroup>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">From</Typography>
              <DatePicker
                value={fromDate}
                onChange={d => setFromDate(d)}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    sx: { width: 140, '& .MuiInputBase-input': { py: '5px', fontSize: '0.8125rem' } },
                  },
                  openPickerButton: { size: 'small', sx: { p: '4px' } },
                }}
              />
              <Typography variant="body2">To</Typography>
              <DatePicker
                value={toDate}
                onChange={d => setToDate(d)}
                minDate={fromDate ?? undefined}
                slotProps={{
                  textField: {
                    size: 'small',
                    variant: 'outlined',
                    sx: { width: 140, '& .MuiInputBase-input': { py: '5px', fontSize: '0.8125rem' } },
                  },
                  openPickerButton: { size: 'small', sx: { p: '4px' } },
                }}
              />
            </Stack>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            size="small"
            onClick={handleClearFilters}
            sx={{ textTransform: 'none', fontSize: '0.75rem', py: '3px', px: '10px' }}
          >
            Clear Filters
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Columns */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Columns
        </Typography>
        <EditColumnsPanel
          availableColumns={modelsValueTypes}
          allValueTypes={modelsValueTypeMap}
          columns={editorColumns}
          onChange={setEditorColumns}
          defaultColumns={DEFAULT_MODELS_COLUMNS}
          requiredColumns={MODELS_REQUIRED_COLUMNS}
        />

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disabled={isNameEmpty}>
          {isEditMode ? 'Save' : 'Create View'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModelsNewViewDialog;
